import { Button, Clickable, Col, Input, Logo, Row, Text } from 'components';
import { firebase, openMobileAppLink, storage, useQuery } from 'core';
import { Api, theme, validateEmail } from 'lib';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppleLogin, FbLogin, GgLogin } from 'screens/Login/components';
import styled from 'styled-components';

/*
  The shared `Checkbox` is built for the marketing pages — a 30px tick absolutely positioned over
  a 20px circle — and at this form's size it spilled over the label. A native control sized to the
  text is the honest fit here.
*/
const ConsentBox = styled.input`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  accent-color: ${theme.SECONDARY};
  cursor: pointer;
`;

/* The label is what most people click, so it carries the cursor and the `for` binding. */
const ConsentLabel = styled.label`
  margin-left: 10px;
  cursor: pointer;
`;

const Auth = ({ handleLoading }) => {
  const [email, setEmail] = useState(''),
    [name, setName] = useState(''),
    [password, setPassword] = useState(''),
    [emailErr, setEmailErr] = useState(null),
    // Optional, and deliberately left out of `disabled` below — an unticked box never blocks
    // registration.
    [sponsorConsent, setSponsorConsent] = useState(false),
    { isMobile, language } = useSelector((state) => state.appState),
    { t } = useTranslation(),
    navigate = useNavigate(),
    dispatch = useDispatch(),
    disabled = password.length < 8 || name.length < 1 || !validateEmail(email),
    query = useQuery();

  async function signUp() {
    const res = await Api.post(
      '/users/signUp',
      { name, email, password, lang: language, sponsorConsent },
      'signUp'
    );
    if (!res) return;
    if (res.err) return setEmailErr(res.err);

    storage.setItem('token', res.token);

    firebase.logEvent('sign_up', { method: 'email' });

    const navigateTo = query.get('navigateTo');
    const redirectUrl = query.get('redirect');

    // Use redirectUrl as navigateTo if present, strip leading slash since User.js adds one
    const finalNavigateTo = 
      redirectUrl
        ? decodeURIComponent(redirectUrl).replace(/^\//, '')
        : navigateTo || 
          (res.user.admin || process.env.REACT_APP_DEFAULT_TEAM_REDIRECT === 'false' 
            ? null 
            : `profile/${process.env.REACT_APP_DEFAULT_TEAM_USERNAME || 'fctokyo'}`);

    dispatch({
      type: 'SET_USER',
      payload: { ...res.user, navigateTo: finalNavigateTo }
    });
  }

  function handleEmailChange(e) {
    setEmail(e.target.value);
    setEmailErr(null);
  }

  return (
    <Col
      pos={isMobile ? 'relative' : 'absolute'}
      pad={isMobile ? '24px 0 0' : '0'}
      noFlex
      bg="#fff"
      wid={isMobile ? '288px' : '296px'}
      minHt="682px"
      top={isMobile ? '0' : '80px'}
      left={isMobile ? '0' : '812px'}
      start
      center
      centerAll
    >
      {!isMobile && <Logo wid="296px" />}

      {isMobile && <Text h2>{t('common.signUp')}</Text>}

      <Input
        marg="32px 0 0"
        wid="100%"
        placeholder={t('common.email')}
        value={email}
        onChange={handleEmailChange}
        error={emailErr}
      />

      <Input
        marg="28px 0 0"
        wid="100%"
        placeholder={t('common.fullName')}
        value={name}
        onChange={(e) => setName(e.target.value)}
      />

      <Input
        marg="28px 0 0"
        wid="100%"
        password
        placeholder={t('common.password')}
        error={!!password.length && password.length < 8 && 'Must at least contain 8 characters.'}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />

      <Row noFlex center wid="100%" marg="24px 0 0">
        <ConsentBox
          id="sponsorConsent"
          type="checkbox"
          checked={sponsorConsent}
          onChange={(e) => setSponsorConsent(e.target.checked)}
        />

        <ConsentLabel htmlFor="sponsorConsent">
          <Text b2 spanned>
            {t('auth.sponsorConsent')}
          </Text>
        </ConsentLabel>
      </Row>

      <Button
        marg="28px 0 0"
        wid="100%"
        text={t('common.signUp')}
        bg={disabled ? theme.GREY_20 : theme.SECONDARY}
        app
        onClick={disabled ? undefined : signUp}
        action="signUp"
      />

      <Text marg="16px 0 0">{t('common.or')}</Text>

      <FbLogin
        handleLoading={handleLoading}
        CustomButton={(props) => (
          <Button facebook onClick={props.onClick} marg="16px 0 0" text={t('common.signUp')} />
        )}
        isSignup
      />

      <GgLogin
        handleLoading={handleLoading}
        CustomButton={(props) => (
          <Button google onClick={props.onClick} marg="16px 0 0" text={t('common.signUp')} />
        )}
        isSignup
      />

      <AppleLogin
        redirectUrl={query.get('redirect')}
        CustomButton={(props) => (
          <Button apple onClick={props.onClick} marg="16px 0 0" text={t('common.signUp')} />
        )}
      />

      <Text b1 marg="16px 0 0">
        Get the APP
      </Text>

      <Row wid="100%" gap="12px" marg="16px 0 0">
        <Clickable
          onClick={() => {
            openMobileAppLink(false);
          }}
        >
          <img src={require('assets/images/app-store.png')} alt="" />
        </Clickable>

        <Clickable
          onClick={() => {
            openMobileAppLink(true);
          }}
        >
          <img src={require('assets/images/google-play.png')} alt="" />
        </Clickable>
      </Row>

      <Clickable
        marg="20px 0 0"
        onClick={() => {
          const redirectUrl = query.get('redirect');
          navigate(redirectUrl ? `/login?redirect=${encodeURIComponent(redirectUrl)}` : '/login');
        }}
      >
        <Text b2>
          {t('auth.alreadyHaveAccount')}{' '}
          <Text spanned b2 med>
            {t('auth.logIn')}
          </Text>
        </Text>
      </Clickable>
    </Col>
  );
};

export default Auth;
